import { useTranslation } from 'next-i18next';
import React from 'react';
import toast from 'react-hot-toast';

import { useArchiveWarehouse } from '@/modules/settings/property/apiHooks';
import AddEditWarehouse from '@/modules/settings/property/components/Warehouses/AddEditWarehouse';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import EditAndDelete from '@/shared/components/atoms/EditAndDelete';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const WarehouseActions: React.FC<{
  warehouse: IWarehouseManagement.WarehouseObjectSchema;
}> = ({ warehouse }) => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { mutate } = useArchiveWarehouse();

  const handleArchive = () => {
    openModal(
      <ConfirmationModal
        variant='danger'
        header={t('deactivate-warehouse-confirmation')}
        message={t('deactivate-warehouse-warning')}
        confirmText={t('deactivate')}
        dismissText={t('back')}
        preferredAction='dismiss'
        reverseButtonOrder
        onConfirm={() =>
          mutate(
            { entityId: selectedAsset?.id, warehouseId: warehouse.id },
            {
              onSuccess: () =>
                toast.success(t('warehouse-archived-successfully')),
            }
          )
        }
      />
    );
  };

  const handleEdit = () => {
    openModal(
      <AddEditWarehouse
        property={selectedAsset}
        warehouseId={warehouse.id}
        disabledCategories={
          new Set(
            warehouse.categories
              .filter((item) => !!item.hasStock)
              .map((item) => item.id)
          )
        }
        defaultValues={{
          name: warehouse.name,
          categories: warehouse.categories.map((item) => item.id),
          responsible: warehouse.responsible.map((item) => item.id),
        }}
      />,
      {
        variant: 'full',
        title: t('edit-warehouse'),
      }
    );
  };

  return (
    <EditAndDelete
      id={warehouse.id}
      onEditClick={handleEdit}
      deleteText={t('deactivate')}
      deleteDisabledMessage={
        !warehouse.canDeactivate ? t('cannot-deactivate-warehouse') : undefined
      }
      onDeleteClick={handleArchive}
    />
  );
};

export default WarehouseActions;
